/**
 * src/lib/voice/voiceProcessFn.js — Default worker processFn for V-4/V-5/V-6.
 *
 * Plan: docs/plans/2026-05-01-voice-sessions-and-image-sync-plan.md.
 *
 * Called by the queue worker for each queued recording. Reads the blob +
 * scope from the recording, dispatches to the right backend handler, and
 * on success transitions the recording to status='ready'.
 *
 * Two backend paths:
 *   - scope_type='project' → uploadVoiceFile(scope_id, file, 'project')
 *     Big files from iPhone Files app, written to Supabase Storage and
 *     registered in voice_uploads. (V-4.)
 *   - scope_type ∈ {task, deal, bundle, general} → captureVoice({ audio_b64,
 *     contextType, contextId, idempotencyKey }) for in-app MediaRecorder
 *     recordings. Writes voice_records + drives the breadcrumb panel via
 *     existing realtime subscription. (V-5/V-6.)
 *
 * Errors thrown here propagate to the worker's processOne, which applies
 * the exponential backoff + retry semantics.
 */
import { uploadVoiceFile, captureVoice, transcribeViaUvtp, insertVoiceRecord, classifyOnly } from '../api/voice-v2.js';
import { updateRecording } from '../voiceQueue.js';
import { PROJECT_UPLOAD_SCOPES, IN_APP_CAPTURE_SCOPES } from './voiceScopes.js';
import { processUvtpScope } from './processUvtpScope.js';
import { logVoiceEvent } from './voiceLogger';

// Whats-next #1 (2026-05-03) — feature-flag-gated routing of project-scope
// recordings through UVTP (`/api/voice-transcribe`) instead of the legacy
// uploadVoiceFile path. Default OFF — set VITE_UVTP_FOR_PROJECTS=1 in
// Vercel env vars to flip the project-scope branch. Other scope_types
// (task / deal / bundle / general) keep the existing captureVoice path
// until UVTP integration is verified end-to-end with real audio.
function uvtpRoutingEnabled() {
  try {
    return import.meta.env?.VITE_UVTP_FOR_PROJECTS === '1';
  } catch {
    return false;
  }
}

// CEO 2026-05-31 — large in-app uploads must NOT take the base64 captureVoice
// path. A >~3 MB blob base64-inflates ~33% → past Vercel's ~4.5 MB serverless
// JSON body cap, so the POST to /api/ai fails and the queue worker retries
// forever (the "Deal · … · Queued" card that never completes, ZERO voice_uploads
// rows). Route large in-app recordings through Supabase Storage (uploadVoiceFile
// → voice_uploads → cron drainer → heavy worker) exactly like project uploads.
//
// Only scopes with a voice_uploads FK home divert today: 'deal' (deal_id column).
// task/bundle/general have no FK column on voice_uploads (and no file-upload
// button — mic only), so they stay on base64 until a migration adds task_id/
// bundle_id. Threshold 3 MB → base64 ≈ 4 MB, safely under the 4.5 MB cap; small
// mic clips keep the fast synchronous base64 path.
const LARGE_INAPP_STORAGE_BYTES = 3 * 1024 * 1024;
const STORAGE_DIVERT_SCOPES = new Set(['deal']);

function shouldDivertInAppToStorage(rec) {
  return (
    STORAGE_DIVERT_SCOPES.has(rec.scope_type) &&
    typeof rec.audio_blob?.size === 'number' &&
    rec.audio_blob.size > LARGE_INAPP_STORAGE_BYTES
  );
}

function asBlob(maybe, mimeType = 'audio/webm') {
  if (typeof Blob !== 'undefined' && maybe instanceof Blob) return maybe;
  // Defensive: structured-clone in some IDB envs (fake-indexeddb in tests,
  // older Safari edge cases) returns a plain object instead of a Blob.
  // Re-wrap so downstream FileReader / File constructors keep working.
  return new Blob([maybe], { type: mimeType });
}

function blobToFile(blob, name) {
  if (typeof File !== 'undefined' && blob instanceof File) return blob;
  const wrapped = asBlob(blob, blob?.type || 'audio/webm');
  return new File([wrapped], name, { type: wrapped.type });
}

function blobToBase64(maybe) {
  return new Promise((resolve, reject) => {
    const blob = asBlob(maybe);
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      const idx = typeof result === 'string' ? result.indexOf(',') : -1;
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('blobToBase64: read failed'));
    reader.readAsDataURL(blob);
  });
}

async function processProjectScope(rec) {
  const file = blobToFile(rec.audio_blob, rec.file_name || 'voice-note');
  // Bug 1 (CEO 2026-05-07): forward rec.recording_id as idempotency_key so the
  // optimistic breadcrumb (emitted by enqueueWithBreadcrumb on file upload)
  // can reconcile with the eventual voice_records row inserted by the cron
  // drainer's kickClassification.
  // TICKET-064 (CEO live 2026-05-07): forward rec.duration_sec so the
  // History card's compact row renders a real duration instead of "—"
  // placeholder. Source: MediaRecorder elapsed time captured in
  // useEntityMic.onstop; null when absent (file picker uploads — separate
  // follow-up to derive from Audio metadata).
  const result = await uploadVoiceFile(
    rec.scope_id,
    file,
    rec.scope_type,
    {
      idempotency_key: rec.recording_id || rec.optimistic_uuid || undefined,
      duration_sec: typeof rec.duration_sec === 'number' ? rec.duration_sec : undefined,
    },
  );
  // CEO 2026-05-03 evening UAT — fix the "card flashed and disappeared"
  // class. The previous code marked status='ready' the moment the upload
  // step succeeded — but only the upload step succeeded. Server-side
  // transcription was kicked off via fire-and-forget POST that iOS Safari
  // killed when the tab backgrounded. Tray's auto-fade then dismissed the
  // card, so the user never learned the transcription failed.
  //
  // New contract: marks 'uploaded' (a distinct IDB-side status meaning
  // "registered with server, awaiting transcription"). useVoiceQueueState
  // polls voice.upload_progress for terminal status and reflects what the
  // server actually did. The Phase A cron drainer (api/ops.js
  // cron.voice_drain_uploads) re-kicks any rows the browser failed to
  // hand off, so iOS-Safari aborts no longer silently fail.
  await updateRecording(rec.recording_id, {
    status: 'uploaded',
    upload_id: result?.upload_id || null,
    audio_blob: null,
  });
}

function uvtpFlagOn() {
  // Vite exposes envs as import.meta.env. Truthy when set to 'true' (string).
  // Default OFF — opt-in only. See docs/plans BCRUMB-UVTP for migration ladder.
  // globalThis.process fallback exists for vitest where vi.stubEnv reliably
  // sets process.env but Vite's import.meta.env transformation can vary
  // mid-test. globalThis is browser-safe (always defined; undefined access
  // resolves to undefined via optional chaining).
  let value;
  try { value = import.meta?.env?.VITE_USE_UVTP_FOR_BREADCRUMBS; } catch { /* ignore */ }
  if (!value) {
    try { value = globalThis?.process?.env?.VITE_USE_UVTP_FOR_BREADCRUMBS; } catch { /* ignore */ }
  }
  // .trim() — Vercel's `vercel env add` from a piped stdin (e.g. `echo true | ...`)
  // bakes the trailing newline into the env value at build time. Without this
  // trim, `'true\n' === 'true'` returns false and the flag stays OFF in prod
  // even when intentionally set. CEO live test 2026-05-05 hit this — the env
  // had been set 10 minutes earlier but uvtpFlagOn() kept returning false.
  return String(value || '').trim().toLowerCase() === 'true';
}

// T3 streaming-STT (spec docs/plans/2026-06-06-streaming-stt-endpoint-spec.md).
// Conversation mode opens a live PCM16 WebSocket to the stt-proxy when this
// flag is ON and a proxy URL is set; otherwise it falls back to the existing
// batch UVTP path. Trim-tolerant for the same Vercel-piped-newline reason as
// uvtpFlagOn() above. Default OFF — opt-in via VITE_STREAMING_STT=true.
export function streamingSttFlagOn() {
  let value;
  try { value = import.meta?.env?.VITE_STREAMING_STT; } catch { /* spine-emit-ok: env read guard, no I/O */ }
  if (!value) {
    try { value = globalThis?.process?.env?.VITE_STREAMING_STT; } catch { /* spine-emit-ok: env read guard, no I/O */ }
  }
  return String(value || '').trim().toLowerCase() === 'true';
}

// Proxy WebSocket URL for the streaming-STT path (wss://<box>/stt-stream).
// Returns '' when unset so callers can treat empty as "streaming disabled".
export function getStreamingSttProxyUrl() {
  let value;
  try { value = import.meta?.env?.VITE_STT_PROXY_URL; } catch { /* spine-emit-ok: env read guard, no I/O */ }
  if (!value) {
    try { value = globalThis?.process?.env?.VITE_STT_PROXY_URL; } catch { /* spine-emit-ok: env read guard, no I/O */ }
  }
  return String(value || '').trim();
}

// VFL-0.2 — STT model quick-switch. The proxy allowlists exactly these three
// names (any unknown name is treated as the env default on the proxy side).
// Export the array so the Settings UI can import it without hardcoding the list
// in a second place.
// llm-registry-ok: src/ runs in browser — cannot use Node.js readFileSync-based getModel().
// These are STT proxy model routing keys (registry: realtime_voice role → gpt-realtime-whisper).
// To update the default: python scripts/update_llm_models.py --set realtime_voice=<new_id>
// then update STREAMING_STT_DEFAULT and STREAMING_STT_MODELS to match.
export const STREAMING_STT_MODELS = [
  'gpt-realtime-whisper', // llm-registry-ok: src/ browser; STT proxy routing key (registry: realtime_voice)
  'gpt-4o-transcribe',    // llm-registry-ok: src/ browser; STT proxy routing key
  'gpt-4o-mini-transcribe', // llm-registry-ok: src/ browser; STT proxy routing key
];

const STREAMING_STT_DEFAULT = 'gpt-realtime-whisper'; // llm-registry-ok: src/ browser; registry: realtime_voice role

/**
 * Read the CEO-selected STT model from localStorage.
 * - Guards typeof localStorage (SSR/service-worker contexts).
 * - Returns the stored value only when it matches the proxy allowlist.
 * - Falls back to the default ('gpt-realtime-whisper') for unset or
 *   unrecognised values so the proxy never receives an unknown name.
 *
 * @returns {string} one of STREAMING_STT_MODELS
 */
export function getStreamingSttModel() {
  try {
    if (typeof localStorage === 'undefined') return STREAMING_STT_DEFAULT;
    const stored = localStorage.getItem('acn_stt_model');
    if (stored && STREAMING_STT_MODELS.includes(stored)) return stored;
  } catch {
    // spine-emit-ok: localStorage access denied in some private-browsing modes — use default
  }
  return STREAMING_STT_DEFAULT;
}

/**
 * Persist the CEO-selected STT model to localStorage.
 * Guards against storage access denial (private-browsing / embedded contexts)
 * so the Settings section never crashes on write.
 *
 * @param {string} model — must be one of STREAMING_STT_MODELS; ignored otherwise.
 */
export function setStreamingSttModel(model) {
  if (!STREAMING_STT_MODELS.includes(model)) return;
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem('acn_stt_model', model);
  } catch {
    // spine-emit-ok: localStorage write denied in some private-browsing modes — silently no-op
  }
}

async function processInAppScope(rec) {
  const base64 = await blobToBase64(rec.audio_blob);

  if (uvtpFlagOn()) {
    // Phase 3 BCRUMB-UVTP — transcript-ASAP architecture.
    //   1. UVTP transcribes audio (~3s, fast path)
    //   2. insertVoiceRecord writes voice_records + voice_transcripts
    //      client-side (~150ms) — both writes guarded by RLS owner=auth.uid().
    //   3. updateRecording marks IDB 'ready' immediately — the breadcrumb
    //      card clears within ~3.5s with the transcript visible.
    //   4. classifyOnly is fired WITHOUT awaiting. The server-side handler
    //      runs the classifier (~6-17s) and UPDATEs voice_records
    //      (intent_type, headline, summary, status='ready'); the realtime
    //      subscription on voice_records re-renders the card with the
    //      classifier output. action_intent + action_result are emitted
    //      to app_error_logs server-side per OBSERVABILITY-FIRST.
    //
    // Sentinel-locked by tests/voice/voiceProcessFn-uvtp-direct-insert.test.js.
    const mimeType = rec.audio_blob?.type || 'audio/webm';
    const uvtp = await transcribeViaUvtp({
      audioBase64: base64,
      mimeType,
      // TICKET-1091: enqueue stores duration_sec (duration_seconds was never written).
      durationSec: rec.duration_sec || undefined,
    });

    const { voice_record_id } = await insertVoiceRecord({
      transcript: uvtp.text,
      contextType: rec.scope_type,
      contextId: rec.scope_id,
      contextHint: rec.context_hint || undefined,
      idempotencyKey: rec.optimistic_uuid || rec.recording_id,
      fileName: rec.file_name || undefined,
      durationSeconds: rec.duration_sec || undefined,
    });

    await updateRecording(rec.recording_id, {
      status: 'ready',
      voice_record_id,
      audio_blob: null,
    });

    // Fire-and-forget classifier kick. Rejection MUST NOT throw out of this
    // function — the worker fired the action; server-side observability
    // captures any failure via action_intent + action_result. Awaiting
    // would block the worker for ~6-17s and double the perceived recording
    // gap on the next breadcrumb.
    classifyOnly(voice_record_id).catch((err) => {
      // eslint-disable-next-line no-console -- visible diagnostic, not silent
      console.warn('[voiceProcessFn] classifyOnly fire-and-forget rejected:', err?.message);
    });

    return;
  }

  // Legacy path (UVTP flag OFF) — captureVoice runs Whisper + classifier
  // server-side and writes voice_records + voice_transcripts itself.
  const result = await captureVoice({
    audio: base64,
    contextType: rec.scope_type,
    contextId: rec.scope_id,
    contextHint: rec.context_hint || undefined,
    language: rec.language || undefined,
    idempotencyKey: rec.optimistic_uuid || rec.recording_id,
    fileName: rec.file_name || undefined,
  });
  // Bug A (CEO live test 2026-05-04) — apiFetch returns {success:false}
  // on POST failure (NO `status` field). The old check `result?.status
  // === 'error'` missed this shape and the worker marked IDB 'ready'
  // with voice_record_id=null — card stuck "Processing…" forever
  // because no realtime UPDATE ever arrived for a non-existent
  // voice_records row.
  // Accept BOTH shapes: apiFetch failure (success:false) AND legacy
  // pipeline-timeout response (status:'error'). Sentinel-locked by
  // tests/voice/voiceProcessFn-uvtp-direct-insert.test.js.
  if (result?.success === false || result?.status === 'error') {
    throw new Error(result?.error || 'captureVoice failed');
  }
  await updateRecording(rec.recording_id, {
    status: 'ready',
    voice_record_id: result?.voice_record_id || null,
    audio_blob: null,
  });
}

export async function voiceProcessFn(rec) {
  if (!rec?.audio_blob) {
    throw new Error('[voiceProcessFn] missing audio_blob on recording ' + (rec?.recording_id || '?'));
  }
  // OBSERVABILITY audit (CEO 2026-05-31): emit the route decision so the queue
  // worker's dispatch is visible on the spine (which path + scope + blob size).
  // Previously the entire client dispatch was dark, so a recording that took the
  // wrong path (e.g. large deal → base64) left no trace. trace_id = recording_id
  // correlates with uploadVoiceFile's storage emits + the eventual server rows.
  const route = PROJECT_UPLOAD_SCOPES.has(rec.scope_type)
    ? (uvtpRoutingEnabled() ? 'uvtp' : 'project_storage')
    : (IN_APP_CAPTURE_SCOPES.has(rec.scope_type)
      ? (shouldDivertInAppToStorage(rec) ? 'inapp_storage_divert' : 'inapp_base64')
      : 'unsupported');
  logVoiceEvent('voice.process.route', 'start', `route=${route}`, {
    trace_id: rec.recording_id || rec.optimistic_uuid || undefined,
    scope_type: rec.scope_type,
    scope_id: rec.scope_id,
    route,
    bytes: typeof rec.audio_blob?.size === 'number' ? rec.audio_blob.size : null,
    // TICKET-1079: container type, so an unsupported/empty file is explainable.
    mime: rec.audio_blob?.type || null,
  });
  if (PROJECT_UPLOAD_SCOPES.has(rec.scope_type)) {
    if (uvtpRoutingEnabled()) {
      return processUvtpScope(rec);
    }
    return processProjectScope(rec);
  }
  if (IN_APP_CAPTURE_SCOPES.has(rec.scope_type)) {
    // Large deal upload → Storage path (processProjectScope is scope-generic:
    // it forwards rec.scope_type to uploadVoiceFile, so the deal lands in
    // voice_uploads with the right scope, never base64 over the 4.5 MB cap).
    if (shouldDivertInAppToStorage(rec)) {
      return processProjectScope(rec);
    }
    return processInAppScope(rec);
  }
  throw new Error(`[voiceProcessFn] unsupported scope_type: ${rec.scope_type}`);
}

